// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Currency, SerializableCurrency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { parseCurrencyAmount } from '@/currency/utilities/validate';

/**
 * Seeds a FormCurrencyTextField field from a whole Currency, reading both the
 * amount and the ISO off it, where toCurrencyInputValueFromString below takes a
 * bare amount string and a separate ISO. A present Currency becomes its 2-decimal
 * form value, and a null/undefined value becomes an empty amount denominated in
 * defaultCurrencyCode (typically the organization default).
 */
export function toCurrencyInputValue(
  value: Currency | null | undefined,
  defaultCurrencyCode: IsoCurrencyCode
): SerializableCurrency {
  return value
    ? toSerializableCurrency(value)
    : { amount: '', isoCode: defaultCurrencyCode };
}

/** Converts a Currency to form state, padded to its minor unit and never rounded. */
export function toSerializableCurrency(value: Currency): SerializableCurrency {
  return {
    amount: padAmountToMinorUnit(value.amount, value.isoCode),
    isoCode: value.isoCode,
  };
}

/**
 * Seeds a FormCurrencyTextField field from a bare amount string off an API
 * response, denominated in currencyCode. Takes the amount on its own, where
 * toCurrencyInputValue above takes a whole Currency and reads the amount from it.
 * The value is zero-padded to the currency's minor unit, never rounded. A null
 * or unparseable amount becomes an empty field.
 */
export function toCurrencyInputValueFromString(
  amount: string | null,
  currencyCode: IsoCurrencyCode
): SerializableCurrency {
  const parsed = amount === null ? null : parseCurrencyAmount(amount);
  return {
    amount: parsed ? padAmountToMinorUnit(parsed, currencyCode) : '',
    isoCode: currencyCode,
  };
}
